import { useState, type FormEvent } from 'react';
import { ApiError, api } from '@/api/client';
import type { Account } from '@/api/types';
import { Alert, Button, Card, Field, Input, Select } from '@/components/ui';
import { useT } from '@/i18n';
import { INDIAN_STATES } from '@/lib/indianStates';
import { billingComplete, billingFormOf, validateBilling, type BillingErrors, type BillingForm } from './billing';

/**
 * Who the invoice is made out to: name, phone and address, plus the state in India (GST place of
 * supply). Asked at checkout, kept on the account so it is asked once. Shows the saved details with
 * an Edit button once complete; while incomplete it is the form, and payment stays off.
 */
export const BillingDetailsCard = ({ account, india, refresh }: { account: Account | null | undefined; india: boolean; refresh: () => Promise<unknown> | void }) => {
  const t = useT();
  const complete = billingComplete(account, india);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<BillingForm>(() => billingFormOf(account));
  const [errors, setErrors] = useState<BillingErrors>({});
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<string>();
  const set = (patch: Partial<BillingForm>) => setForm((prev) => ({ ...prev, ...patch }));
  const showForm = !complete || editing;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const result = validateBilling(form, india);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setBusy(true);
    setApiError(undefined);
    try {
      await api.me.update({ billing: result.billing, ...(result.state ? { billingState: result.state } : {}) });
      await refresh();
      setEditing(false);
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const err = (field: keyof BillingErrors) => (errors[field] ? t(errors[field]!) : undefined);

  if (!showForm && account?.billing) {
    const b = account.billing;
    const stateName = INDIAN_STATES.find((s) => s.code === account.billingState)?.name;
    return (
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-label text-ink">{t('checkout.billing.title')}</h2>
            <p className="mt-2 text-body text-ink">{b.name}</p>
            <p className="text-body text-ink-soft">
              {[b.addressLine1, b.addressLine2, `${b.city} ${b.postalCode}`.trim(), india ? stateName : undefined].filter(Boolean).join(', ')}
            </p>
            <p className="text-body text-ink-soft">{b.phone}</p>
          </div>
          <Button variant="tertiary" onClick={() => { setForm(billingFormOf(account)); setEditing(true); }}>
            {t('checkout.billing.edit')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={(e) => void onSubmit(e)} className="grid gap-4" aria-labelledby="billing-title" noValidate>
        <div>
          <h2 id="billing-title" className="text-label text-ink">{t('checkout.billing.title')}</h2>
          <p className="mt-1 text-caption text-ink-muted">{t('checkout.billing.hint')}</p>
        </div>
        {apiError && <Alert tone="warn">{apiError}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="billing-name" label={t('checkout.billing.name')} error={err('name')}>
            <Input value={form.name} maxLength={120} autoComplete="name" onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field id="billing-phone" label={t('checkout.billing.phone')} error={err('phone')}>
            <Input type="tel" value={form.phone} maxLength={20} autoComplete="tel" inputMode="tel" onChange={(e) => set({ phone: e.target.value })} />
          </Field>
        </div>
        <Field id="billing-address1" label={t('checkout.billing.address1')} error={err('addressLine1')}>
          <Input value={form.addressLine1} maxLength={160} autoComplete="address-line1" onChange={(e) => set({ addressLine1: e.target.value })} />
        </Field>
        <Field id="billing-address2" label={t('checkout.billing.address2')}>
          <Input value={form.addressLine2} maxLength={160} autoComplete="address-line2" onChange={(e) => set({ addressLine2: e.target.value })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="billing-city" label={t('checkout.billing.city')} error={err('city')}>
            <Input value={form.city} maxLength={80} autoComplete="address-level2" onChange={(e) => set({ city: e.target.value })} />
          </Field>
          <Field id="billing-postal" label={t('checkout.billing.postal')} error={err('postalCode')}>
            <Input value={form.postalCode} maxLength={12} autoComplete="postal-code" inputMode="numeric" onChange={(e) => set({ postalCode: e.target.value })} />
          </Field>
        </div>
        {india && (
          <Field id="checkout-state" label={t('checkout.state')} hint={t('checkout.stateHint')} error={err('state')}>
            <Select value={form.state} onChange={(e) => set({ state: e.target.value })}>
              <option value="" disabled>
                {t('checkout.statePlaceholder')}
              </option>
              {INDIAN_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="flex flex-wrap gap-3">
          <Button type="submit" loading={busy}>
            {t('checkout.billing.save')}
          </Button>
          {complete && (
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
};
